<section
  class="relative z-50 flex min-h-[calc(100vh-220px)] justify-center px-4 py-12 lg:px-8"
  aria-label="Login account transition"
>
  <div class="relative w-full max-w-xl">
    <div
      class="relative mx-auto w-full max-w-2xl rounded-2xl bg-white p-8 text-black shadow-xl md:p-10"
    >
      <!-- Logo -->
      <div class="mb-8 flex">
        <a routerLink="/" class="inline-block" title="Go to homepage">
          <img
            src="assets/images/logos/CB_Logo_Color_RGB.png"
            alt="Comcast Business logo"
            class="h-16 w-auto"
          />
        </a>
      </div>

      <h2 class="text-2xl font-semibold">
        @if (currentStep() === 'form') {
          Secure your account in one step
        } @else {
          Check your email
        }
      </h2>

      @if (currentStep() === 'form') {
        <div class="mt-2 font-lato">
          <p class="mx-auto leading-6 text-[#4b5563]">
            We're moving to an industry-standard login platform — so your
            account stays protected with the best available security.
          </p>

          <div class="mt-6 rounded-xl border border-[#e5e7eb] bg-[#e2eafc] p-4">
            <p class="text-lg font-semibold text-[#111827]">Why update now</p>

            <ul class="mt-3 space-y-2 text-[#4b5563]">
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-500"
                  aria-hidden="true"
                ></span>
                Multi-factor authentication built in
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-500"
                  aria-hidden="true"
                ></span>
                Faster, simpler password recovery
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-500"
                  aria-hidden="true"
                ></span>
                Invite and manage team members
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-500"
                  aria-hidden="true"
                ></span>
                Required for MyPortal access after October 2026
              </li>
            </ul>
          </div>

          <form
            [formGroup]="formGroup"
            class="mt-8"
            novalidate
            (ngSubmit)="submit()"
          >
            <label
              for="transitionEmailId"
              class="comcast-input-label text-base"
            >
              Email address
            </label>

            <input
              formControlName="email"
              id="transitionEmailId"
              type="email"
              required
              name="email"
              autocomplete="email"
              aria-describedby="email-error"
              [attr.aria-invalid]="messages().emailInput.length > 0"
              [ngClass]="{
                'comcast-input-invalid': messages().emailInput.length > 0,
              }"
              class="comcast-input transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder="name@company.com"
            />

            <div
              id="email-error"
              role="alert"
              aria-live="polite"
              class="comcast-validation-error"
              [ngClass]="{ hidden: messages().emailInput.length < 1 }"
            >
              {{ messages().emailInput }}
            </div>

            @if (messages().emailInput.length === 0) {
              <p class="mt-1 text-sm leading-5 text-gray-600">
                This becomes your new login username. You'll get a confirmation
                email to activate.
              </p>
            }

            @if (errorMessage()) {
              <div
                class="comcast-validation-error mt-4 text-base"
                role="alert"
                aria-live="polite"
              >
                {{ errorMessage() }}
              </div>
            }

            <div
              class="mt-4 w-full"
              [ngClass]="{ 'mt-4': errorMessage(), 'mt-8': !errorMessage() }"
            >
              <button
                type="submit"
                class="btn-primary w-full justify-center shadow-md transition hover:shadow-lg"
                [disabled]="isLoading()"
                [attr.aria-busy]="isLoading()"
                [attr.aria-label]="
                  isLoading() ? 'Submitting, please wait' : 'Confirm email'
                "
              >
                @if (isLoading()) {
                  <div class="flex min-w-12 justify-center">
                    <mat-spinner [diameter]="20"></mat-spinner>
                  </div>
                } @else {
                  {{ errorMessage() ? 'Try again' : 'Confirm email' }}
                }
              </button>

              <button
                type="button"
                class="btn-secondary mt-2 w-full justify-center opacity-80 transition hover:opacity-100"
                [disabled]="isLoading()"
                routerLink="/dashboard"
              >
                I'll do this later
              </button>
            </div>

            <p class="mt-6 text-center text-sm leading-5 text-gray-400">
              By continuing, you agree to the Comcast Business
              <a
                href="https://business.comcast.com/enterprise/terms-conditions/nitel"
                target="_blank"
                rel="noopener noreferrer"
                title="Terms of Service"
                class="font-lato text-sm"
                >Terms of Service</a
              >
              and
              <a
                href="https://business.comcast.com/privacy/nitel-privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                title="Privacy Policy"
                class="font-lato text-sm"
                >Privacy Policy.
              </a>
              and applicable terms.
            </p>
          </form>
        </div>
      } @else if (currentStep() === 'confirmation') {
        <div class="mt-2 font-lato">
          <p>
            We sent a link to activate your new login. Takes about 2 minutes to
            finish setup.
          </p>

          <div class="my-8 rounded-xl border border-[#dbeafe] bg-[#eff6ff] p-4">
            <p class="flex items-center gap-2 text-xl leading-6 text-[#1f2937]">
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full bg-green-500"
                aria-hidden="true"
              ></span>
              <span class="font-bold">{{ activationEmail() }}</span>
            </p>
          </div>

          <ol aria-label="Steps to complete your login setup">
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-[#1f2937]"
                  >1</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="pb-4 leading-7 text-[#4b5563]"
                >Open the email from
                <strong class="font-semibold text-[#1f2937]"
                  >noreply&#64;idbroker.comcast.com</strong
                >
                and click
                <strong class="font-semibold text-[#1f2937]"
                  >Activate Comcast account</strong
                ></span
              >
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-[#1f2937]"
                  >2</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="pb-4 leading-6 text-[#4b5563]">
                <strong class="font-semibold text-[#1f2937]"
                  >Create a new password</strong
                >
                for your login
                <br />
                <span class="text-sm text-gray-400"
                  >You won't use your old MyPortal password after this.</span
                >
              </span>
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-[#1f2937]"
                  >3</span
                >
              </div>
              <span class="pb-4 leading-6 text-[#4b5563]">
                <strong class="font-semibold text-[#1f2937]"
                  >Set up multi-factor authentication (MFA)</strong
                >
                <br />
                <span class="text-sm text-gray-400"
                  >Use an authenticator app &mdash; keeps your account secure
                  every time you sign in.</span
                >
              </span>
            </li>
          </ol>

          @if (errorMessage()) {
            <div
              class="comcast-validation-error mt-4 text-base"
              role="alert"
              aria-live="polite"
            >
              {{ errorMessage() }}
            </div>
          }

          <div class="mt-4 flex w-full items-baseline justify-between gap-4">
            <p class="font-medium text-gray-400">Didn't receive the email?</p>
            <button
              type="button"
              class="btn-secondary rounded-none border-0 px-2"
              [disabled]="isLoading()"
              [attr.aria-busy]="isLoading()"
              [attr.aria-label]="
                isLoading() ? 'Sending email, please wait' : 'Resend email'
              "
              (click)="resendEmail()"
            >
              @if (isLoading()) {
                <div class="flex min-w-12 justify-center px-2">
                  <mat-spinner
                    [diameter]="20"
                    class="[&_circle]:!stroke-black"
                  ></mat-spinner>
                </div>
              } @else {
                Resend email
                <mat-icon aria-hidden="true">email</mat-icon>
              }
            </button>
          </div>
          <p class="-mt-2 text-sm text-gray-400">
            Check your spam folder if you don't see it within a minute.
          </p>

          <button
            type="button"
            class="btn-secondary mt-8 w-full justify-center"
            [disabled]="isLoading()"
            (click)="signOut()"
          >
            Done
          </button>

          <p class="mt-2 text-center text-sm text-gray-400" aria-live="polite">
            You'll be logged out automatically in
            <strong>{{ migrationTimer.countdown() }}s</strong>
          </p>
        </div>
      }
    </div>
  </div>
</section>
